import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLang } from "../i18n";
import ChapterHeading from "./ChapterHeading";
import { scrollTo } from "./Nav";

function ArtworkItem({ item, index, prefix, onSelect, ratioOverride }) {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const label = item[lang];

  return (
    <motion.figure
      ref={ref}
      data-testid={`${prefix}-artwork-${index}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group cursor-pointer ${item.className || ""}`}
      onClick={() => onSelect(index)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onSelect(index)}
    >
      <div className={`relative overflow-hidden border border-black/15 ${ratioOverride || item.ratio}`}>
        <motion.div style={{ y }} className="absolute inset-0">
          <img
            src={item.img}
            alt={label.title}
            loading="lazy"
            className="h-full w-full object-cover scale-[1.14] grayscale-[30%] sepia-[12%] transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.22] group-hover:grayscale-0 group-hover:sepia-0"
          />
        </motion.div>
        {item.sold && (
          <span
            data-testid={`${prefix}-sold-badge-${index}`}
            className="absolute top-4 left-4 z-10 bg-linen/95 border border-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-ink"
          >
            {t.sold}
          </span>
        )}
        {!item.sold && item.price && (
          <span
            data-testid={`${prefix}-price-badge-${index}`}
            className="absolute top-4 left-4 z-10 bg-linen/95 border border-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-ink"
          >
            {item.price}
          </span>
        )}
      </div>
      <figcaption className="mt-4 flex items-baseline justify-between gap-4">
        <span className="font-serif text-xl md:text-2xl group-hover:text-klein transition-colors duration-300">
          {label.title}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/50 text-right">
          {[label.medium, item.year].filter(Boolean).join(" · ")}
        </span>
      </figcaption>
    </motion.figure>
  );
}

function Lightbox({ item, lang, soldLabel, onClose, onPrev, onNext }) {
  const { t } = useLang();
  const label = item[lang];
  const photos = [item.img, ...(item.extra || [])];
  const [photoIdx, setPhotoIdx] = useState(0);
  useEffect(() => setPhotoIdx(0), [item.img]);

  const enquire = () => {
    window.dispatchEvent(new CustomEvent("prefill-contact", { detail: t.enquireMsg.replace("{title}", label.title) }));
    onClose();
    setTimeout(() => scrollTo("#contact"), 350);
  };
  return (
    <motion.div
      data-testid="lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[90] bg-linen flex flex-col"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-5 md:px-10 py-5 border-b border-black/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-5 min-w-0">
          <span className="font-serif font-light uppercase text-lg md:text-xl text-ink tracking-tight">
            {label.title}
          </span>
          {!item.sold && (
            <button
              data-testid="lightbox-enquire-btn"
              onClick={enquire}
              className="shrink-0 border border-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-klein hover:border-klein hover:text-linen"
            >
              {t.enquire}
            </button>
          )}
        </div>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          aria-label="Close"
          className="text-black/60 hover:text-terra transition-colors duration-300"
        >
          <X size={26} strokeWidth={1.5} />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-5 md:px-24">
        <motion.img
          key={photos[photoIdx]}
          data-testid="lightbox-image"
          src={photos[photoIdx]}
          alt={label.title}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className={`max-h-full max-w-full object-contain ${photos.length > 1 ? "cursor-pointer" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (photos.length > 1) setPhotoIdx((p) => (p + 1) % photos.length);
          }}
        />
      </div>
      {photos.length > 1 && (
        <div data-testid="lightbox-photo-nav" className="flex justify-center gap-2 pb-4" onClick={(e) => e.stopPropagation()}>
          {photos.map((_, i) => (
            <button
              key={i}
              data-testid={`lightbox-photo-dot-${i}`}
              onClick={() => setPhotoIdx(i)}
              aria-label={`Photo ${i + 1}`}
              className={`h-1.5 w-6 transition-colors duration-300 ${i === photoIdx ? "bg-terra" : "bg-black/20 hover:bg-black/50"}`}
            />
          ))}
        </div>
      )}
      <div className="flex items-center justify-between px-5 md:px-10 py-6 border-t border-black/10" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="lightbox-prev"
          onClick={onPrev}
          aria-label="Previous"
          className="text-black/60 hover:text-terra transition-colors duration-300"
        >
          <ChevronLeft size={28} strokeWidth={1.5} />
        </button>
        <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-black/50 text-center px-4">
          {item.sold ? `${label.medium} · ${soldLabel}` : [label.medium, item.price].filter(Boolean).join(" · ")}
        </p>
        <button
          data-testid="lightbox-next"
          onClick={onNext}
          aria-label="Next"
          className="text-black/60 hover:text-terra transition-colors duration-300"
        >
          <ChevronRight size={28} strokeWidth={1.5} />
        </button>
      </div>
    </motion.div>
  );
}

export default function Gallery({ id, chapter, items, testidPrefix, shared, uniformRatio, twoCol }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open === null) return;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o + 1) % items.length);
      if (e.key === "ArrowLeft") setOpen((o) => (o - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open !== null, items.length]);

  const c = t[chapter];
  return (
    <section id={id} data-testid={`${testidPrefix}-section`} className="py-32 md:py-48">
      <ChapterHeading num={c.num} title={c.title} note={c.note} testid={`${testidPrefix}-heading`} />
      {c.sizes && (
        <div className="px-5 md:px-10 mb-20 md:mb-28 -mt-8 md:-mt-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl flex flex-col gap-4"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-black/50">
              {c.sizesIntro}
            </p>
            {c.sizes.map((s, i) => (
              <p key={i} data-testid={`tapestry-size-${i}`} className="font-serif font-light text-xl md:text-2xl text-black/80">
                <span className="inline-block w-28 md:w-36 font-mono text-[11px] uppercase tracking-[0.25em] text-terra align-middle">
                  {s.label}
                </span>
                {s.dim} <span className="whitespace-nowrap">— {s.price}</span>
              </p>
            ))}
          </motion.div>
        </div>
      )}
      <div className={uniformRatio ? "px-5 md:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" : "px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8"}>
        {items.map((item, i) => (
          <ArtworkItem
            key={i}
            item={uniformRatio ? { ...item, className: "" } : twoCol ? { ...item, className: i % 2 === 0 ? "md:col-span-5 md:col-start-1" : "md:col-span-6 md:col-start-7" } : item}
            index={i}
            prefix={testidPrefix}
            onSelect={setOpen}
            ratioOverride={uniformRatio}
          />
        ))}
      </div>
      <AnimatePresence>
        {open !== null && (
          <Lightbox
            item={{ ...items[open], extra: [...(items[open].extra || []), ...(shared || [])] }}
            lang={lang}
            soldLabel={t.sold}
            onClose={() => setOpen(null)}
            onPrev={() => setOpen((o) => (o - 1 + items.length) % items.length)}
            onNext={() => setOpen((o) => (o + 1) % items.length)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
